import { useCallback, useEffect, useRef, useState } from 'react';
import type { MarketTrade } from '../api/useMarketTrades';

const FLASH_DURATION_MS = 1_500;
/** More than this arriving at once is a bulk load, not news — don't flash it. */
const MAX_FLASH_BURST = 3;

/**
 * Which live trades are new enough to flash. Skips the first batch (backlog, not
 * news) and any burst over MAX_FLASH_BURST (a reconnect), and remembers what has
 * flashed so scrolling a row back into view doesn't replay it.
 */
export function useNewTradeFlash(liveTrades: MarketTrade[]): {
    flashIds: Set<string>;
    hasFlashed: (id: string) => boolean;
} {
    const knownIdsRef = useRef<Set<string>>(new Set());
    const flashedRef = useRef<Set<string>>(new Set());
    const [flashIds, setFlashIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        const newIds: string[] = [];
        for (const t of liveTrades) {
            if (!knownIdsRef.current.has(t.id)) newIds.push(t.id);
        }

        const nextKnown = new Set<string>();
        for (const t of liveTrades) nextKnown.add(t.id);
        const isFirstLoad =
            knownIdsRef.current.size === 0 && nextKnown.size > 0;
        knownIdsRef.current = nextKnown;

        if (isFirstLoad || newIds.length === 0) return;
        if (newIds.length > MAX_FLASH_BURST) return;

        setFlashIds(prev => {
            const next = new Set(prev);
            for (const id of newIds) next.add(id);
            return next;
        });

        const timer = setTimeout(() => {
            setFlashIds(prev => {
                const next = new Set(prev);
                for (const id of newIds) next.delete(id);
                return next;
            });
            for (const id of newIds) flashedRef.current.add(id);
        }, FLASH_DURATION_MS);

        return () => clearTimeout(timer);
    }, [liveTrades]);

    // Stable identity: safe to put in a caller's dep array.
    const hasFlashed = useCallback(
        (id: string) => flashedRef.current.has(id),
        []
    );

    return { flashIds, hasFlashed };
}
